import {
  type DposCryptoFlow,
  type DposSessionDetails,
  type DposSessionStatus,
} from "../../services/dposService";
import {
  buildUniversalWalletPaymentUri,
  preferUniversalPaymentUri,
} from "../../utils/walletPaymentUri";

const RAW_EVM_ADDRESS = /^0x[a-fA-F0-9]{40}$/;

export type DposSessionMode = "88pay" | "paylink" | DposCryptoFlow;

export interface DposStatusPresentation {
  label: string;
  tone: "warning" | "info" | "success" | "danger" | "neutral";
}

export interface DposInfoRow {
  label: string;
  value: string;
}

const STATUS_PRESENTATION: Record<DposSessionStatus, DposStatusPresentation> = {
  active: { label: "Waiting for payment", tone: "warning" },
  pending: { label: "Waiting for payment", tone: "warning" },
  processing: { label: "Processing payment", tone: "info" },
  paid: { label: "Payment received", tone: "success" },
  confirmed: { label: "Payment received", tone: "success" },
  expired: { label: "QR code expired", tone: "neutral" },
  cancelled: { label: "Payment cancelled", tone: "danger" },
  failed: { label: "Payment failed", tone: "danger" },
};

const TERMINAL_STATUSES: DposSessionStatus[] = [
  "paid",
  "confirmed",
  "expired",
  "cancelled",
  "failed",
];

export function resolveDposSessionId(
  session: DposSessionDetails | null | undefined
): string | null {
  return session?.id || session?._id || null;
}

export function resolveDposSessionStatus(
  status: DposSessionDetails["status"]
): DposSessionStatus {
  switch (status) {
    case "pending":
    case "processing":
    case "paid":
    case "confirmed":
    case "expired":
    case "cancelled":
    case "failed":
      return status;
    case "active":
    default:
      return "active";
  }
}

export function isTerminalDposSessionStatus(
  status: DposSessionDetails["status"]
): boolean {
  return TERMINAL_STATUSES.includes(resolveDposSessionStatus(status));
}

export function getDposStatusPresentation(
  status: DposSessionDetails["status"]
): DposStatusPresentation {
  return STATUS_PRESENTATION[resolveDposSessionStatus(status)];
}

export function resolveDposQrValue(
  session: DposSessionDetails | null | undefined,
  mode: DposSessionMode
): string | null {
  if (!session) {
    return null;
  }

  const candidates =
    mode === "88pay"
      ? [
          session.qrPayload,
          session.qrCodeValue,
          session.paymentLink,
          session.deepLink,
          session.deeplink,
          session.qrCode,
        ]
      : mode === "paylink"
        ? [
            session.paymentLink,
            session.qrPayload,
            session.qrCodeValue,
            session.qrCode,
            session.deepLink,
            session.deeplink,
          ]
        : mode === "internal_wallet"
          ? [
              session.deepLink,
              session.deeplink,
              session.qrPayload,
              session.qrCodeValue,
              session.paymentLink,
              session.qrCode,
            ]
          : [
              session.externalWalletQr,
              session.qrPayload,
              session.qrCodeValue,
              session.qrCode,
              session.paymentLink,
            ];

  return (
    candidates.find(
      (value): value is string =>
        typeof value === "string" && value.trim().length > 0
    ) || null
  );
}

export function resolveDposDeepLink(
  session: DposSessionDetails | null | undefined
): string | null {
  const deepLink =
    session?.deepLink || session?.deeplink || session?.paymentLink;
  return typeof deepLink === "string" && deepLink.trim().length > 0
    ? deepLink
    : null;
}

export function resolveDposPaymentLink(
  session: DposSessionDetails | null | undefined
): string | null {
  const directLink = session?.paymentLink;
  if (typeof directLink === "string" && directLink.trim().length > 0) {
    return directLink.trim();
  }

  const fallbackCandidates = [
    session?.qrCode,
    session?.qrCodeValue,
    session?.qrPayload,
  ];

  const urlCandidate = fallbackCandidates.find(
    (value): value is string =>
      typeof value === "string" && /^https?:\/\//i.test(value.trim())
  );

  return urlCandidate ? urlCandidate.trim() : null;
}

export function resolveDposReference(
  session: DposSessionDetails | null | undefined
): string | null {
  const reference =
    session?.paymentReference ||
    session?.merchantReference ||
    session?.reference;

  if (typeof reference === "string" && reference.trim().length > 0) {
    return reference;
  }

  const sessionId = resolveDposSessionId(session);
  return sessionId ? sessionId.slice(-8).toUpperCase() : null;
}

export function resolveDposPayId(
  session: DposSessionDetails | null | undefined
): string | null {
  const payId = session?.payId;
  return typeof payId === "string" && payId.trim().length > 0
    ? payId.trim().toUpperCase()
    : null;
}

export function resolveDposWalletAddress(
  session: DposSessionDetails | null | undefined
): string | null {
  const address = session?.walletAddress || session?.address;
  return typeof address === "string" && address.trim().length > 0
    ? address
    : null;
}

export function resolveDposNetwork(
  session: DposSessionDetails | null | undefined
): string | null {
  const network = session?.network || session?.chainName;
  return typeof network === "string" && network.trim().length > 0
    ? network
    : null;
}

export function resolveDposCryptoAsset(
  session: DposSessionDetails | null | undefined
): string | null {
  const asset =
    session?.cryptoAsset || session?.cryptoCurrency || session?.assetSymbol;
  return typeof asset === "string" && asset.trim().length > 0
    ? asset.toUpperCase()
    : null;
}

export function resolveDposCryptoAmount(
  session: DposSessionDetails | null | undefined
): string | null {
  const rawAmount = session?.cryptoAmount;

  if (typeof rawAmount === "number" && Number.isFinite(rawAmount)) {
    return String(rawAmount);
  }

  if (typeof rawAmount === "string" && rawAmount.trim().length > 0) {
    return rawAmount.trim();
  }

  return null;
}

export function buildDposInfoRows(
  session: DposSessionDetails | null | undefined,
  mode: DposSessionMode
): DposInfoRow[] {
  const rows: DposInfoRow[] = [];
  const reference = resolveDposReference(session);
  const payId = resolveDposPayId(session);
  const walletAddress = resolveDposWalletAddress(session);
  const network = resolveDposNetwork(session);
  const asset = resolveDposCryptoAsset(session);
  const cryptoAmount = resolveDposCryptoAmount(session);

  if (mode === "external_wallet") {
    if (walletAddress) {
      rows.push({ label: "Wallet address", value: walletAddress });
    }
    if (network) {
      rows.push({ label: "Network", value: network });
    }
    if (asset && cryptoAmount) {
      rows.push({ label: "Amount", value: `${cryptoAmount} ${asset}` });
    }
    if (reference) {
      rows.push({ label: "Reference", value: reference });
    }
    return rows;
  }

  if (mode === "88pay") {
    if (payId && reference && reference !== payId) {
      rows.push({ label: "Reference", value: reference });
    }

    return rows;
  }

  if (mode === "paylink") {
    if (reference) {
      rows.push({ label: "Reference", value: reference });
    }
  }

  if (mode === "internal_wallet") {
    if (walletAddress) {
      rows.push({ label: "Wallet address", value: walletAddress });
    }

    if (reference) {
      rows.push({ label: "Reference", value: reference });
    }
  }

  if (asset && cryptoAmount) {
    rows.push({ label: "Estimated amount", value: `${cryptoAmount} ${asset}` });
  }

  return rows;
}

export interface DposExternalWalletQrContext {
  assetSymbol: string | null;
  chainLabel: string | null;
  amountCrypto?: string | null;
}

export function resolveDposExternalWalletDisplayQr(
  session: DposSessionDetails | null | undefined,
  mode: DposSessionMode,
  context: DposExternalWalletQrContext
): string | null {
  if (mode !== "external_wallet") {
    return resolveDposQrValue(session, mode);
  }

  const backend = resolveDposQrValue(session, mode);
  const addr =
    resolveDposWalletAddress(session) ||
    (typeof backend === "string" && RAW_EVM_ADDRESS.test(backend.trim())
      ? backend.trim()
      : null);
  const chainLabel = context.chainLabel || resolveDposNetwork(session) || null;
  const asset = context.assetSymbol || resolveDposCryptoAsset(session);
  const amount =
    context.amountCrypto ?? resolveDposCryptoAmount(session) ?? null;

  const clientUri =
    addr != null
      ? buildUniversalWalletPaymentUri({
          address: addr,
          assetSymbol: asset,
          chainLabel,
          amountCrypto: amount,
        })
      : null;

  return preferUniversalPaymentUri(backend, clientUri);
}
